import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
    ReferenceBookResponse,
    UserActivationApiService,
    UserApiService,
    UserAttributesApiService,
    UserCredentialDeliveryStatus,
    UserPageResponse,
    UserResponse,
    UserTag,
} from '@npck/af-frontend-api';
import {LocalizationService, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {EMPTY, mergeMap, Subject, Subscription, zip} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterField,
    FilterSelectField,
} from '../../../components/full-table/filter/filter.component';
import {mergeTableEvent, TableEvent} from '../../../components/full-table/full-table.component';
import {INITIAL_PAGE_NUMBER, INITIAL_PAGE_SIZE} from '../../../components/full-table/paging/paginator.component';
import {Column, ColumnType, CustomLayoutColumn, Row, StatusColumn} from '../../../components/full-table/table/table.component';
import {StatusStyle} from '../../../components/status/status.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {DictionaryHolderService} from '../../../domain/dictionary-holder.service';
import {PasswordExpiryStatusService} from '../../../domain/password-expiry-status.service';
import {USER_ID} from '../../../domain/query-params.keys';
import {UsersFilterHolderService} from '../../../domain/users-filter-holder.service';
import {Path} from '../../../path';
import {AdminPanelTab} from '../admin.component';

@Component({
    selector: 'af-users',
    templateUrl: 'users.component.html',
    standalone: false
})
export class UsersComponent implements OnInit, OnDestroy {
    @Input() buttonClickStream: Subject<AdminPanelTab>;
    @Output() createUserClickListener = new EventEmitter<void>();
    @Output() changeButtonText = new EventEmitter<string>();

    tableEvent: TableEvent;
    filterFields: FilterField[];
    dataSource: Row[] = [];
    totalElements: number;
    loadDataSubscription: Subscription;
    buttonClickSubscription: Subscription;
    isDestroyed = false;
    organizations: ReferenceBookResponse[];

    constructor(
        private userApiService: UserApiService,
        private userActivationApiService: UserActivationApiService,
        private userAttributesApiService: UserAttributesApiService,
        private routerService: RouterService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private dictionaryHolderService: DictionaryHolderService,
        private filterHolderService: UsersFilterHolderService,
        private localizationService: LocalizationService,
        private passwordExpiryStatusService: PasswordExpiryStatusService,
        private authHolderService: AuthHolderService,
    ) {
        this.filterFields = this.buildFilterFields();
    }

    get isOrganizationScoped(): boolean {
        return this.authHolderService.isOrganizationScopedAdmin;
    }

    ngOnInit(): void {
        this.buttonClickSubscription = this.buttonClickStream.subscribe(activeTab =>
            this.routerService.navigateToTab<AdminPanelTab>(Path.ADMIN_PANEL_USERS_CREATE, activeTab));
        this.loadUsers(this.filterHolderService.getState().tableEvent);
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
        this.loadDataSubscription.unsubscribe();
        this.buttonClickSubscription?.unsubscribe();
    }

    loadUsers(tableEvent: TableEvent): void {
        this.tableEvent = mergeTableEvent(this.filterHolderService.getState().tableEvent, tableEvent);
        this.filterHolderService.storeTableState(this.tableEvent);
        this.loadDataSubscription?.unsubscribe();
        if (this.tableEvent.spinnerId !== 'paging') {
            this.dataSource = [];
            this.totalElements = 0;
        }

        const organizationsRequest = this.dictionaryHolderService.getReference('bin');
        const enabledFilter = this.tableEvent.filter?.enabled && fieldExtractor(this.tableEvent.filter?.enabled)[0];
        const usersRequest = this.userAttributesApiService.getUsers({
            tag: this.tableEvent.filter?.tag && fieldExtractor(this.tableEvent.filter?.tag)[0] as UserTag,
            limit: this.tableEvent.page?.pageSize,
            offset: this.tableEvent.page?.pageOffset,
            search: this.tableEvent.filter?.query,
            isDeleted: enabledFilter ? enabledFilter === 'false' : undefined,
            bin: this.tableEvent.filter?.organization && fieldExtractor(this.tableEvent.filter?.organization)[0],
            credentialDeliveryStatus: this.tableEvent.filter?.credentialDeliveryStatus
                && fieldExtractor(this.tableEvent.filter?.credentialDeliveryStatus)[0] as UserCredentialDeliveryStatus,
        });
        this.loadDataSubscription = zip([organizationsRequest, usersRequest])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([organizations, usersPageResponse]) => {
                this.organizations = organizations;
                this.filterHolderService.storeResponse(usersPageResponse);
                const buttonText =
                    this.tableEvent.spinnerId !== 'initial' || usersPageResponse.items.length > 0 ? 'i18n_af_buttonCreateUser' : '';
                this.changeButtonText.emit(buttonText);
                this.buildDataSource(usersPageResponse);
            });
    }

    private buildDataSource(response: UserPageResponse): void {
        this.totalElements = response.total;
        this.dataSource = response.items.map(item => <Row>{
            click: () => this.openUserDetails(item.id),
            actions: <CustomLayoutColumn>{
                type: ColumnType.CUSTOM_LAYOUT,
                cellStyle: {
                    layoutDirection: 'row',
                    layoutAlignFirst: 'start',
                    layoutAlignSecond: 'center',
                    layoutGap: '8px',
                },
                layoutContentItems: [
                    {
                        type: 'icon',
                        text: 'ic_edit',
                        click: () => this.editUser(item.id),
                        tooltip: 'i18n_af_labelEdit',
                        iconSize: 'i-16',
                    },
                    ...(!item.isDeleted ? [{
                        type: 'icon',
                        text: 'ic_delete',
                        click: () => this.deleteUser(item),
                        tooltip: 'i18n_af_labelDeactivate',
                        iconSize: 'i-16',
                    }] : [{
                        type: 'icon',
                        text: 'ic_unarchive',
                        click: () => this.reactivate(item),
                        tooltip: 'i18n_af_labelReactivate',
                        iconSize: 'i-16',
                    }]),
                    ...(!item.isDeleted && item.mustChangePassword ? [{
                        type: 'icon',
                        text: 'ic_message',
                        click: () => this.resendOtp(item),
                        tooltip: 'i18n_af_labelResendOtp',
                        iconSize: 'i-16',
                    }] : []),
                ],
            },
            isEnabled: <StatusColumn>{
                type: ColumnType.STATUS,
                text: !item.isDeleted ? 'i18n_af_statusEnabled' : 'i18n_af_statusDisabled',
                style: !item.isDeleted ? StatusStyle.SUCCESS : StatusStyle.ERROR,
            },
            login: <Column>{
                type: ColumnType.TEXT,
                text: item.username,
            },
            userFullName: <Column>{
                type: ColumnType.TEXT,
                text: [item.lastName, item.firstName].filter(n => !!n).join(' '),
            },
            iin: <Column>{
                type: ColumnType.TEXT,
                text: item.iin,
            },
            email: <Column>{
                type: ColumnType.TEXT,
                text: item.email,
            },
            ...(this.isOrganizationScoped ? {} : {
                organization: <Column>{
                    type: ColumnType.TEXT,
                    text: this.organizations.find(organization => organization.code === item.bin)?.name_ru || item.bin,
                },
            }),
            credentialDelivery: <StatusColumn>{
                type: ColumnType.STATUS,
                text: this.deliveryStatusText(item.credentialDeliveryStatus),
                style: this.deliveryStatusStyle(item.credentialDeliveryStatus),
            },
            createdDate: <Column>{
                type: ColumnType.DATE,
                text: item.createdDate,
            },
            createdByUser: <Column>{
                type: ColumnType.TEXT,
                text: item.createdBy,
            },
            lastLoginDateTime: <Column>{
                type: ColumnType.DATETIME,
                text: item.lastLoginDateTime,
            },
            passwordExpiry: <StatusColumn>{
                type: ColumnType.STATUS,
                ...this.passwordExpiryStatusService.status(item.passwordExpiryDaysLeft),
            },
            deletedDate: <Column>{
                type: ColumnType.DATE,
                text: item.deletedDate,
            },
            deletedByUser: <Column>{
                type: ColumnType.TEXT,
                text: item.deletedBy,
            },
            deactivationReason: <Column>{
                type: ColumnType.TEXT,
                text: this.deactivationReasonText(item.deactivationReason),
            },
        });
    }

    private buildFilterFields(): FilterField[] {
        const tagFilter = <FilterSelectField<UserTag>>{
            isMultiSelect: false,
            type: 'select',
            name: 'tag',
            values: [UserTag.SYSTEM, UserTag.PERSON],
        };
        const organizationFilter = <FilterDictionaryField>{
            isMultiSelect: false,
            type: 'select',
            name: 'organization',
            dictionaryName: 'bin',
            values: [],
        };
        return [
            {
                type: 'text',
                name: 'query',
            },
            ...(this.isOrganizationScoped ? [] : [tagFilter]),
            <FilterSelectField<string>>{
                isMultiSelect: false,
                type: 'select',
                name: 'enabled',
                values: ['true', 'false'],
            },
            ...(this.isOrganizationScoped ? [] : [organizationFilter]),
            <FilterSelectField<UserCredentialDeliveryStatus>>{
                isMultiSelect: false,
                type: 'select',
                name: 'credentialDeliveryStatus',
                values: [UserCredentialDeliveryStatus.SENT, UserCredentialDeliveryStatus.FAILED],
            },
        ];
    }

    private deactivationReasonText(reason?: string): string {
        return reason ? this.localizationService.getLocalizedKey(`i18n_af_deactivationReason#${reason}`) : '';
    }

    private deliveryStatusText(status?: UserCredentialDeliveryStatus): string {
        return status === UserCredentialDeliveryStatus.FAILED ? 'i18n_af_credentialDeliveryFailed' : 'i18n_af_credentialDeliverySent';
    }

    private deliveryStatusStyle(status?: UserCredentialDeliveryStatus): StatusStyle {
        return status === UserCredentialDeliveryStatus.FAILED ? StatusStyle.ERROR : StatusStyle.SUCCESS;
    }

    private reactivate(user: UserResponse): void {
        const fullName = [user.lastName, user.firstName].filter(n => !!n).join(' ');
        this.dialogService.openDialog({
            title: 'i18n_af_reactivateDialogTitle',
            subtitle: 'i18n_af_reactivateDialogSubtitle',
            buttonOkText: 'i18n_af_buttonConfirm',
            buttonCancelText: 'i18n_af_buttonCancel',
            translationParams: {
                fullName: fullName,
                login: user.username,
            },
        }).pipe(
            mergeMap(dialogResult => !dialogResult && EMPTY || this.userActivationApiService.reactivateUser(user.id)
                .pipe(rxSpinner('reactivating'))),
        )
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageUserReactivated', 'success', false, {
                    fullName: fullName,
                    login: user.username,
                });
                this.loadUsers({
                    page: {
                        pageSize: INITIAL_PAGE_SIZE,
                        pageNumber: INITIAL_PAGE_NUMBER,
                        pageOffset: INITIAL_PAGE_NUMBER * INITIAL_PAGE_SIZE,
                    },
                    spinnerId: 'initial',
                });
            });
    }

    private resendOtp(user: UserResponse): void {
        const fullName = [user.lastName, user.firstName].filter(n => !!n).join(' ');
        this.dialogService.openDialog({
            title: 'i18n_af_resendOtpDialogTitle',
            subtitle: 'i18n_af_resendOtpDialogSubtitle',
            buttonOkText: 'i18n_af_buttonSend',
            buttonCancelText: 'i18n_af_buttonCancel',
            translationParams: {
                fullName: fullName,
                login: user.username,
            },
        }).pipe(
            mergeMap(dialogResult => !dialogResult && EMPTY || this.userApiService.sendUserOtp(user.id)
                .pipe(rxSpinner('resendingOtp'))),
        )
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageOtpResent', 'success', false, {
                    fullName: fullName,
                    login: user.username,
                });
            });
    }

    private openUserDetails(id: string): void {
        this.routerService.navigateToTab<AdminPanelTab>(`${Path.ADMIN_PANEL_USERS}/${id}`, 'tabUsers');
    }

    private editUser(id: string): void {
        this.routerService.navigateToTab<AdminPanelTab>(`${Path.ADMIN_PANEL_USERS_EDIT.replace(`:${USER_ID}`, id)}`, 'tabUsers');
    }

    private deleteUser(user: UserResponse): void {
        this.dialogService.openDialog({
            title: 'i18n_af_userDeactivationDialogTitle',
            subtitle: 'i18n_af_userDeactivationDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDeactivate',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {
                fullName: [user.lastName, user.firstName].filter(n => !!n).join(' '),
                login: user.username,
            },
        }).pipe(
            mergeMap(dialogResult => !dialogResult && EMPTY || this.userActivationApiService.deactivateUser(user.id)
                .pipe(rxSpinner('deleting'))),
        )
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageUserDeactivated', 'success', false, {
                    fullName: [user.lastName, user.firstName].filter(n => !!n).join(' '),
                    login: user.username,
                });
                this.loadUsers({
                    page: {
                        pageSize: INITIAL_PAGE_SIZE,
                        pageNumber: INITIAL_PAGE_NUMBER,
                        pageOffset: INITIAL_PAGE_NUMBER * INITIAL_PAGE_SIZE,
                    },
                    spinnerId: 'initial',
                });
            });
    }
}
